/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Play, Clock, Video, Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';
import { listMyAppointments } from '@/lib/telemedicine';
import { initials } from '@/lib/utils-cat';
import { dayLabelIt, timeLabelIt, URGENCY_META, pickNextAppointment, useCountdown } from './hero/heroUtils';
import { EmptyVisitHero } from './hero/EmptyVisitHero';
import { CountdownChips } from './hero/CountdownChips';

/**
 * Sprint 9G — Cinematic Patient Home.
 *
 * Hero card that spotlights the patient's next telemedicine visit with a live
 * countdown, doctor avatar and 1-click join. Empty state, countdown e helper
 * vivono in `./hero/*`.
 */

const PatientNextVisitHero = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);

  const load = async () => {
    try {
      const r = await listMyAppointments('patient');
      setAppointments(r.appointments || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const next = useMemo(() => pickNextAppointment(appointments), [appointments]);

  const targetMs = next?._dt || null;
  const cd = useCountdown(targetMs);
  const canJoin = next && (next.status === 'in_progress' || (cd && cd.inWindow));

  if (loading) {
    return (
      <Card className="p-5 rounded-3xl bg-gradient-to-br from-primary/10 via-card to-card border-primary/25 animate-pulse min-h-[128px]"
        data-testid="patient-hero-loading" />
    );
  }

  if (!next) return <EmptyVisitHero />;

  // ---------- Next visit spotlight ----------
  const start = new Date(next._dt);
  const isToday = new Date().toDateString() === start.toDateString();
  const urgency = next.urgency || 'routine';
  const urgencyMeta = URGENCY_META[urgency] || URGENCY_META.routine;
  const doctorName = next.doctor_name || 'Medico';
  const shortDoctor = doctorName.startsWith('Dr') ? doctorName : `Dr. ${doctorName}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid="patient-next-visit-hero"
    >
      <Card className="relative overflow-hidden p-0 rounded-3xl border-primary/25">
        {/* Cinematic background: aurora + grain */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/8 to-card" />
        <motion.div aria-hidden
          className="absolute -top-24 -left-20 h-56 w-56 rounded-full bg-primary/30 blur-3xl"
          animate={{ scale: [1, 1.15, 1], opacity: [0.35, 0.6, 0.35] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div aria-hidden
          className="absolute -bottom-24 -right-16 h-48 w-48 rounded-full bg-emerald-400/25 blur-3xl"
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />

        <div className="relative p-5 md:p-6 grid md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-center">
          <div className="min-w-0">
            <motion.div
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary/70"
            >
              {canJoin ? (
                <>
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-600 font-semibold">LIVE — Pronto ad accoglierti</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3" />
                  <span>{isToday ? 'Oggi' : 'Prossima visita'}</span>
                </>
              )}
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
              className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-1"
            >
              {canJoin ? 'La tua visita ti aspetta' : shortDoctor}
            </motion.h2>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
              className="mt-2 flex items-center gap-3 text-sm text-muted-foreground flex-wrap"
            >
              <Avatar className="h-9 w-9 ring-2 ring-primary/25">
                {next.doctor_avatar && <AvatarImage src={next.doctor_avatar} />}
                <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                  {initials(doctorName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col leading-tight">
                <span className="font-semibold text-foreground">{shortDoctor}</span>
                {next.doctor_specialty && (
                  <span className="text-xs">{next.doctor_specialty}</span>
                )}
              </div>
              <Badge className={`${urgencyMeta.cls} border-0`}>{urgencyMeta.label}</Badge>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="mt-3 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 sm:gap-3 items-center"
            >
              <div className="inline-flex items-center gap-1.5 text-sm">
                <Clock className="h-3.5 w-3.5 text-primary" />
                <span className="font-semibold">
                  {isToday ? `Oggi ${timeLabelIt(start)}` : `${dayLabelIt(start)} — ${timeLabelIt(start)}`}
                </span>
              </div>

              {!canJoin && cd && !cd.past && <CountdownChips cd={cd} />}
            </motion.div>

            {next.reason && (
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                className="mt-2 text-xs text-muted-foreground line-clamp-2"
              >
                <b>Motivo:</b> {next.reason}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground"
            >
              <ShieldCheck className="h-3 w-3 text-primary" />
              <span>Video end-to-end · Nessuna registrazione</span>
            </motion.div>
          </div>

          {/* CTA column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 18 }}
            className="flex md:flex-col items-center md:items-end gap-2 md:gap-3"
          >
            {canJoin ? (
              <Button
                size="lg"
                onClick={() => navigate(`/telemedicine/room/${next.id}`)}
                className="rounded-full font-semibold w-full md:w-auto shadow-lg shadow-primary/25"
                data-testid="patient-hero-join-btn"
              >
                <Play className="h-4 w-4" /> Entra in visita
              </Button>
            ) : (
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(`/telemedicine/room/${next.id}`)}
                className="rounded-full w-full md:w-auto border-primary/50 hover:bg-primary/10"
                data-testid="patient-hero-open-btn"
              >
                <Video className="h-4 w-4" /> Apri sala d'attesa
              </Button>
            )}
            <button
              type="button"
              onClick={() => document.querySelector('[data-testid="appointments-list"]')?.scrollIntoView({ behavior: 'smooth' })}
              className="hidden md:inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid="patient-hero-see-all"
            >
              Tutte le visite <ArrowRight className="h-3 w-3" />
            </button>
          </motion.div>
        </div>
      </Card>
    </motion.div>
  );
};

export default PatientNextVisitHero;
